// src/components/InterestIncome.js
import React, { useEffect, useState } from 'react';
import { Percent, HelpCircle, Globe, PlusCircle, Trash2 } from 'react-feather';

function InterestIncome({ formData, onDataChange }) {
  
  const [showDetails, setShowDetails] = useState(false);
  const [showSavingsBondExclusion, setShowSavingsBondExclusion] = useState(false);

  // --- 1. Helpers for Dynamic Lists ---
  const handleAddPayer = (listName) => {
    const currentList = formData[listName] || [];
    const newItem = { id: Date.now(), name: '', amount: '', type: 'Standard', address: '', ssn: '' };
    onDataChange({ target: { name: listName, value: [...currentList, newItem] } });
  };

  const handleRemovePayer = (listName, id) => {
    const currentList = formData[listName] || [];
    const updatedList = currentList.filter(item => item.id !== id);
    onDataChange({ target: { name: listName, value: updatedList } });
  };

  const handlePayerChange = (listName, id, field, value) => {
    const currentList = formData[listName] || [];
    const updatedList = currentList.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    );
    onDataChange({ target: { name: listName, value: updatedList } });
  };

  // --- 2. Auto-Expand Logic ---
  useEffect(() => {
    const interest = parseFloat(formData.taxableInterest) || 0;
    const dividends = parseFloat(formData.ordinaryDividends) || 0;
    
    if ((interest > 1500 || dividends > 1500 || formData.foreignAccounts) && !showDetails) {
      setShowDetails(true);
    }
  }, [formData.taxableInterest, formData.ordinaryDividends, formData.foreignAccounts]);

  // --- 3. Auto-Calculate Totals ---
  useEffect(() => {
    const intList = formData.interestPayers || [];
    const divList = formData.dividendPayers || [];

    if (intList.length > 0) {
        const line2_Total = intList.reduce((sum, item) => {
        const amt = parseFloat(item.amount) || 0;
        if (['Nominee', 'AccruedInterest', 'OID', 'ABP'].includes(item.type)) return sum - amt;
        return sum + amt;
        }, 0);
        
        const line3_Excludable = parseFloat(formData.excludableSavingsBondInterest) || 0;
        const line4_Taxable = Math.max(0, line2_Total - line3_Excludable);

        if (line4_Taxable !== parseFloat(formData.taxableInterest || 0)) {
            onDataChange({ target: { name: 'taxableInterest', value: line4_Taxable } });
        }
    }

    if (divList.length > 0) {
        const totalDiv = divList.reduce((sum, item) => {
        const amt = parseFloat(item.amount) || 0;
        if (item.type === 'Nominee') return sum - amt;
        return sum + amt;
        }, 0);

        if (totalDiv !== parseFloat(formData.ordinaryDividends || 0)) {
            onDataChange({ target: { name: 'ordinaryDividends', value: totalDiv } });
        }
    }
  }, [formData.interestPayers, formData.dividendPayers, formData.excludableSavingsBondInterest, onDataChange]);

  const handleRadioChange = (e) => {
    const booleanValue = e.target.value === 'true';
    onDataChange({ target: { name: e.target.name, value: booleanValue, type: 'radio' } });
  };

  return (
    <div className="form-section" style={{ marginTop: '2rem' }}>
      <div className="content-box-header">
        <div className="header-title-with-help">
          <h2><Percent size={20} className="icon-orange" /> Interest & Dividends</h2>
        </div>
      </div>

      {/* --- QUESTION CARD --- */}
      <div className="dependent-card" style={{ marginBottom: '1.5rem' }}>
        <div className="form-group">
          <label style={{ fontWeight: 600, color: '#554633ff' }}>Did you have over $1,500 in interest or dividends, receive seller-financed mortgage interest, or had a foreign account?</label>
          
          <div className="radio-group">
            <input 
              type="radio" 
              id="schedB_no" 
              checked={!showDetails} 
              onChange={() => setShowDetails(false)} 
            />
            <label htmlFor="schedB_no">No (Enter Totals Only)</label>

            <input 
              type="radio" 
              id="schedB_yes" 
              checked={showDetails} 
              onChange={() => setShowDetails(true)} 
            />
            <label htmlFor="schedB_yes">Yes (List Payers)</label>
          </div>
        </div>
      </div>

      {/* --- SIMPLE ENTRY SECTION (Shown if No) --- */}
      {!showDetails && (
        <div className="dependent-card" style={{ animation: 'fadeIn 0.3s' }}>
            <div className="form-row">
                <div className="form-group">
                    <label style={{ fontWeight: 600, color: '#554433ff' }}>Total Interest Income</label>
                    <div className="input-with-prefix">
                        <span className="prefix">$</span>
                        <input 
                            type="number" 
                            name="taxableInterest" 
                            value={formData.taxableInterest ?? ''} 
                            onChange={onDataChange} 
                            placeholder="0" 
                        />
                    </div>
                    <small>Form 1099-INT, Box 1</small>
                </div>
                <div className="form-group">
                    <label style={{ fontWeight: 600, color: '#554433ff' }}>Total Ordinary Dividends</label>
                    <div className="input-with-prefix">
                        <span className="prefix">$</span>
                        <input 
                            type="number" 
                            name="ordinaryDividends" 
                            value={formData.ordinaryDividends ?? ''} 
                            onChange={onDataChange} 
                            placeholder="0" 
                        />
                    </div>
                    <small>Form 1099-DIV, Box 1a</small>
                </div>
            </div>

            {/* --- NEW: Qualified Dividends Input --- */}
            <div className="form-row" style={{ marginTop: '1rem' }}>
                <div className="form-group">
                    <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                        <label style={{ fontWeight: 600, color: '#554433ff' }}>Qualified Dividends</label>
                        <div className="has-tooltip header-help-icon" data-tooltip="Dividends that qualify for lower capital gains tax rates. Found in Box 1b of Form 1099-DIV.">
                            <HelpCircle size={16} />
                        </div>
                    </div>
                    <div className="input-with-prefix">
                        <span className="prefix">$</span>
                        <input 
                            type="number" 
                            name="qualifiedDividends" 
                            value={formData.qualifiedDividends ?? ''} 
                            onChange={onDataChange} 
                            placeholder="0" 
                        />
                    </div>
                    <small>Form 1099-DIV, Box 1b (Included in Total above)</small>
                </div>
            </div>

            {/* --- NEW: Line 3c Checkboxes --- */}
            <div className="form-group" style={{ marginTop: '1rem', padding: '10px', backgroundColor: '#fcfaf8ff', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#554433ff' }}>Line 3c: Child's Dividends (Form 8814)</label>
                <div className="checkbox-group" style={{ marginTop: '0.5rem' }}>
                    <input 
                        type="checkbox" 
                        id="childsQualifiedDividendsIncluded" 
                        name="childsQualifiedDividendsIncluded" 
                        checked={formData.childsQualifiedDividendsIncluded || false} 
                        onChange={onDataChange} 
                    />
                    <label htmlFor="childsQualifiedDividendsIncluded">Check if child's dividends are included in <strong>Line 3a</strong> (Qualified)</label>
                </div>
                <div className="checkbox-group">
                    <input 
                        type="checkbox" 
                        id="childsOrdinaryDividendsIncluded" 
                        name="childsOrdinaryDividendsIncluded" 
                        checked={formData.childsOrdinaryDividendsIncluded || false} 
                        onChange={onDataChange} 
                    />
                    <label htmlFor="childsOrdinaryDividendsIncluded">Check if child's dividends are included in <strong>Line 3b</strong> (Ordinary)</label>
                </div>
            </div>
        </div>
      )}

      {/* --- DETAILED LIST SECTION (Shown if Yes) --- */}
      {showDetails && (
        <div style={{ animation: 'fadeIn 0.3s' }}>
            
            {/* PART I: INTEREST LIST */}
            <div className="dependent-card" style={{ marginTop: '1.5rem' }}>
                <div className="content-box-header" style={{ marginBottom: '1rem', borderBottom: '1px solid #f0e9e2ff', paddingBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, color: '#695547ff' }}>Part I: Interest Payers</h4>
                    <button onClick={() => handleAddPayer('interestPayers')} className="add-item-button" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                        <PlusCircle size={14} /> Add Payer
                    </button>
                </div>

                {(formData.interestPayers || []).map((payer, index) => (
                <div key={payer.id} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px dashed #f0e8e2ff' }}>
                    <div className="form-row" style={{ alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.85rem' }}>Type</label>
                        <select 
                        value={payer.type || 'Standard'} 
                        onChange={(e) => handlePayerChange('interestPayers', payer.id, 'type', e.target.value)}
                        style={{ fontSize: '0.9rem', padding: '8px' }}
                        >
                        <option value="Standard">Standard (Bank)</option>
                        <option value="SellerFinanced">Seller-Financed</option>
                        <option value="Nominee">Nominee (Subtract)</option>
                        <option value="AccruedInterest">Accrued (Subtract)</option>
                        <option value="OID">OID (Subtract)</option>
                        <option value="ABP">Bond Premium (Subtract)</option>
                        </select>
                    </div>
                    <div className="form-group" style={{ flex: 2 }}>
                        <label style={{ fontSize: '0.85rem' }}>Payer Name</label>
                        <input 
                        type="text" 
                        value={payer.name} 
                        onChange={(e) => handlePayerChange('interestPayers', payer.id, 'name', e.target.value)}
                        placeholder="e.g. Chase Bank"
                        />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.85rem' }}>Amount</label>
                        <input 
                        type="number" 
                        value={payer.amount} 
                        onChange={(e) => handlePayerChange('interestPayers', payer.id, 'amount', e.target.value)}
                        placeholder="0"
                        style={['Nominee', 'AccruedInterest', 'OID', 'ABP'].includes(payer.type) ? { color: '#ef4444' } : {}}
                        />
                    </div>
                    <button onClick={() => handleRemovePayer('interestPayers', payer.id)} className="delete-card-button" style={{ marginBottom: '10px', color: '#ef4444' }}><Trash2 size={18} /></button>
                    </div>
                    {payer.type === 'SellerFinanced' && (
                        <div className="form-row" style={{ marginTop: '0.5rem', backgroundColor: '#fff7ed', padding: '10px', borderRadius: '6px' }}>
                            <div className="form-group" style={{ flex: 2 }}>
                                <label style={{ fontSize: '0.8rem' }}>Buyer's Address</label>
                                <input type="text" value={payer.address || ''} onChange={(e) => handlePayerChange('interestPayers', payer.id, 'address', e.target.value)} placeholder="Address" />
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label style={{ fontSize: '0.8rem' }}>Buyer's SSN</label>
                                <input type="text" value={payer.ssn || ''} onChange={(e) => handlePayerChange('interestPayers', payer.id, 'ssn', e.target.value)} placeholder="Optional" />
                            </div>
                        </div>
                    )}
                </div>
                ))}
            </div>

            {/* PART II: DIVIDEND LIST */}
            <div className="dependent-card" style={{ marginTop: '1.5rem' }}>
                <div className="content-box-header" style={{ marginBottom: '1rem', borderBottom: '1px solid #f0e7e2ff', paddingBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, color: '#695647ff' }}>Part II: Dividend Payers</h4>
                    <button onClick={() => handleAddPayer('dividendPayers')} className="add-item-button" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                        <PlusCircle size={14} /> Add Payer
                    </button>
                </div>
                {(formData.dividendPayers || []).map((payer, index) => (
                <div key={payer.id} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px dashed #f0e9e2ff' }}>
                    <div className="form-row" style={{ alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.85rem' }}>Type</label>
                        <select value={payer.type || 'Standard'} onChange={(e) => handlePayerChange('dividendPayers', payer.id, 'type', e.target.value)} style={{ fontSize: '0.9rem', padding: '8px' }}>
                        <option value="Standard">Standard</option>
                        <option value="Nominee">Nominee (Subtract)</option>
                        </select>
                    </div>
                    <div className="form-group" style={{ flex: 2 }}>
                        <label style={{ fontSize: '0.85rem' }}>Payer Name</label>
                        <input type="text" value={payer.name} onChange={(e) => handlePayerChange('dividendPayers', payer.id, 'name', e.target.value)} placeholder="e.g. Vanguard" />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.85rem' }}>Amount</label>
                        <input type="number" value={payer.amount} onChange={(e) => handlePayerChange('dividendPayers', payer.id, 'amount', e.target.value)} placeholder="0" style={payer.type === 'Nominee' ? { color: '#ef4444' } : {}} />
                    </div>
                    <button onClick={() => handleRemovePayer('dividendPayers', payer.id)} className="delete-card-button" style={{ marginBottom: '10px', color: '#ef4444' }}><Trash2 size={18} /></button>
                    </div>
                </div>
                ))}

                {/* --- NEW: Qualified Dividends Input (Detailed View) --- */}
                <div className="form-group" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#fff7ed', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                    <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                        <label style={{ fontWeight: 600, color: '#9a3412' }}>Total Qualified Dividends</label>
                        <div className="has-tooltip header-help-icon" data-tooltip="Enter the total Qualified Dividends from Box 1b of all your 1099-DIV forms.">
                            <HelpCircle size={16} color="#9a3412" />
                        </div>
                    </div>
                    <div className="input-with-prefix">
                        <span className="prefix">$</span>
                        <input 
                            type="number" 
                            name="qualifiedDividends" 
                            value={formData.qualifiedDividends ?? ''} 
                            onChange={onDataChange} 
                            placeholder="0" 
                            style={{ borderColor: '#fed7aa' }}
                        />
                    </div>
                </div>
            </div>

            {/* PART III: FOREIGN ACCOUNTS */}
            <div className="dependent-card" style={{ marginTop: '1.5rem', backgroundColor: '#fcfaf8ff' }}>
                <div className="content-box-header" style={{ marginBottom: '1rem', borderBottom: 'none', paddingBottom: 0 }}>
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Globe size={18} className="icon-orange" /> Part III: Foreign Accounts
                    </h4>
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ fontSize: '0.9rem' }}>7a. Did you have a financial interest in a foreign account?</label>
                    <div className="radio-group">
                        <input type="radio" id="foreignAcctNo" name="foreignAccounts" value="false" checked={formData.foreignAccounts === false} onChange={handleRadioChange} />
                        <label htmlFor="foreignAcctNo">No</label>
                        <input type="radio" id="foreignAcctYes" name="foreignAccounts" value="true" checked={formData.foreignAccounts === true} onChange={handleRadioChange} />
                        <label htmlFor="foreignAcctYes">Yes</label>
                    </div>
                </div>
                {formData.foreignAccounts && (
                    <div className="form-group" style={{ marginLeft: '1rem', borderLeft: '3px solid #f0e6e2ff', paddingLeft: '1rem' }}>
                        <label style={{ fontSize: '0.9rem' }}>If "Yes", are you required to file FinCEN Form 114 (FBAR)?</label>
                        <div className="radio-group">
                            <input type="radio" id="fbarNo" name="fileFbar" value="false" checked={formData.fileFbar === false} onChange={handleRadioChange} />
                            <label htmlFor="fbarNo">No</label>
                            <input type="radio" id="fbarYes" name="fileFbar" value="true" checked={formData.fileFbar === true} onChange={handleRadioChange} />
                            <label htmlFor="fbarYes">Yes</label>
                        </div>
                        <div style={{ marginTop: '1rem' }}>
                            <label style={{ fontSize: '0.9rem' }}>Enter the name of the foreign country:</label>
                            <input type="text" name="foreignAccountCountry" value={formData.foreignAccountCountry || ''} onChange={onDataChange} placeholder="e.g. Switzerland" style={{ marginTop: '0.5rem' }} />
                        </div>
                    </div>
                )}
                <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label style={{ fontSize: '0.9rem' }}>8. Did you receive a distribution from a foreign trust?</label>
                    <div className="radio-group">
                        <input type="radio" id="foreignTrustNo" name="foreignTrust" value="false" checked={formData.foreignTrust === false} onChange={handleRadioChange} />
                        <label htmlFor="foreignTrustNo">No</label>
                        <input type="radio" id="foreignTrustYes" name="foreignTrust" value="true" checked={formData.foreignTrust === true} onChange={handleRadioChange} />
                        <label htmlFor="foreignTrustYes">Yes</label>
                    </div>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}

export default InterestIncome;